import { useTranslation } from '@/lib/i18n';
import { Link, useNavigate, useSearch, isNotFound } from '@tanstack/react-router';
import { usePortalContext } from '@/lib/use-portal-context';
import { FilterYearIcon } from './assets/filter-year-icon';
import { categoriesKeys } from '@worldcoo/monolith/core';
import { FilterCategoryIcon } from './assets/filter-category-icon';
import { NavigationMenuLink } from '@worldcoo/ui/components/navigation-menu';
import { Separator } from '@worldcoo/ui/components/separator';
import { Button } from '@worldcoo/ui/components/button';
import { CheckIcon, XIcon, ZapIcon } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { Suspense, type ReactNode } from 'react';
import { usePortalCampaignsQuery } from '@/lib/queries';
import { SidebarContent, SidebarFooter, SidebarHeader, useSidebar } from '@worldcoo/ui/components/sidebar';
import { Title } from '@worldcoo/ui/components/form';
import { useIsFilterApplied } from '@/lib/use-filter-applied';

export function CampaignsFiltersSidebar() {
	return (
		<Suspense>
			<SuspensedCampaignsFiltersSidebar />
		</Suspense>
	);
}

function SuspensedCampaignsFiltersSidebar() {
	const { i18n } = useTranslation();
	const navigate = useNavigate({ from: '/$portalId/' });
	const sidebar = useSidebar();
	const isFilterApplied = useIsFilterApplied();

	const handleClose = () => {
		sidebar.setOpen(false);
		sidebar.setOpenMobile(false);
	};

	const clearFilters = () => {
		navigate({ search: { year: undefined, causes: undefined }, resetScroll: false });
		handleClose();
	};

	return (
		<SidebarContent className="bg-background">
			<SidebarHeader className="flex-row items-center justify-between border-b">
				<Title className="px-2 py-1 text-2xl">{i18n.actions.filter}</Title>
				<Button type="button" variant="ghost" size="icon" onClick={handleClose}>
					<XIcon className="size-7" strokeWidth={1} />
					<span className="sr-only">{i18n.actions.close}</span>
				</Button>
			</SidebarHeader>

			<DivWithPrimaryColorVariable className="scrollbar flex flex-1 flex-col gap-6 overflow-y-scroll px-3 py-6 lg:flex-none">
				<div className="flex flex-col gap-3">
					<div className="flex gap-3">
						<FilterYearIcon className="text-primary size-6" />
						{i18n.filters.year}
					</div>
					<YearFilter className="flex-row flex-wrap p-0" />
				</div>

				<Separator />

				<div className="flex flex-col gap-3">
					<div className="flex gap-3">
						<FilterCategoryIcon className="text-primary size-6" />
						{i18n.filters.cause}
					</div>

					<CausesFilter className="p-0" />
				</div>
			</DivWithPrimaryColorVariable>

			{isFilterApplied && (
				<SidebarFooter className="border-t p-0">
					<DivWithPrimaryColorVariable className="flex gap-3 p-3">
						<Button type="button" className="w-full shrink" onClick={clearFilters}>
							{i18n.actions.clear}
						</Button>
					</DivWithPrimaryColorVariable>
				</SidebarFooter>
			)}
		</SidebarContent>
	);
}

// MARK: Year Filter
function YearFilter(props: { className?: string; isNavigationMenu?: boolean }) {
	const { portalId } = usePortalContext();
	const { year } = useSearch({ from: '/$portalId/' });
	const campaigns = usePortalCampaignsQuery();

	if (isNotFound(campaigns)) return null;

	const WrappedLink = ({ children }: { children: ReactNode }) =>
		props.isNavigationMenu ? <NavigationMenuLink asChild>{children}</NavigationMenuLink> : <>{children}</>;

	return (
		<ul className={cn('flex w-72 flex-wrap gap-2 p-3', props.className)}>
			{campaigns.yearOptions.map((yearOption) => (
				<WrappedLink key={yearOption}>
					<Link
						to="/$portalId"
						params={{ portalId }}
						search={(prev) => ({ ...prev, year: yearOption === year ? undefined : yearOption })}
						resetScroll={false}
						className="aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground flex h-10 flex-auto grow-0 items-center justify-center rounded-sm border px-2.5 text-sm"
					>
						{yearOption}
					</Link>
				</WrappedLink>
			))}
		</ul>
	);
}

// MARK: State Filter
export function StateFilter(props: { className?: string }) {
	const { portalId } = usePortalContext();
	const { i18n } = useTranslation();
	const { state } = useSearch({ from: '/$portalId/' });
	const campaigns = usePortalCampaignsQuery();

	if (isNotFound(campaigns)) return null;

	const { campaignsCount, activeCampaignsCount, filteredCampaignsCount, filteredActiveCampaignsCount } = campaigns;
	const finishedCampaignsCount = campaignsCount - activeCampaignsCount;
	const finishedFilteredCampaignsCout = filteredCampaignsCount - filteredActiveCampaignsCount;

	if (!portalId) return;
	if (activeCampaignsCount === 0 || finishedCampaignsCount === 0) return;

	return (
		<ul className={cn('bg-background shadow-3xl flex w-full gap-2 rounded-xl p-1 lg:w-auto', props.className)}>
			<Link
				to="/$portalId"
				params={{ portalId }}
				search={(prev) => ({ ...prev, state: undefined })}
				activeOptions={{ exact: true }}
				resetScroll={false}
				className="aria-[current=page]:bg-primary/10 flex w-full flex-row items-center justify-center gap-3 rounded-lg p-2 text-nowrap transition-colors"
			>
				<ZapIcon
					className={cn(
						'bg-primary/20 text-primary size-5 shrink-0 rounded-full p-1',
						!state && 'text-primary-foreground bg-primary',
					)}
				/>
				{i18n.filters.active} ({filteredActiveCampaignsCount})
			</Link>

			<Link
				to="/$portalId"
				params={{ portalId }}
				search={(prev) => ({ ...prev, state: 'finished' })}
				resetScroll={false}
				className="aria-[current=page]:bg-primary/10 flex w-full flex-row items-center justify-center gap-3 rounded-lg p-2 text-nowrap transition-colors"
			>
				<CheckIcon
					className={cn(
						'bg-primary/20 text-primary size-5 shrink-0 rounded-full p-1',
						state === 'finished' && 'bg-primary text-primary-foreground',
					)}
					strokeWidth={4}
				/>
				{i18n.filters.finished} ({finishedFilteredCampaignsCout})
			</Link>
		</ul>
	);
}

// MARK: Causes Filter
function CausesFilter(props: { className?: string; isNavigationMenu?: boolean }) {
	const { portalId } = usePortalContext();
	const { i18n } = useTranslation();
	const { causes } = useSearch({ from: '/$portalId/' });

	if (!portalId) return;

	const WrappedLink = ({ children }: { children: ReactNode }) =>
		props.isNavigationMenu ? <NavigationMenuLink asChild>{children}</NavigationMenuLink> : <>{children}</>;

	return (
		<ul className={cn('flex w-72 flex-wrap gap-2 p-3', props.className)}>
			{categoriesKeys.map((cause) => (
				<WrappedLink key={cause}>
					<Link
						to="/$portalId"
						params={{ portalId }}
						search={(prev) => ({
							...prev,
							causes: causes?.includes(cause) ? causes.filter((c) => c !== cause) : [...(causes ?? []), cause],
						})}
						aria-current={causes?.includes(cause) ? 'page' : undefined}
						resetScroll={false}
						className="aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground flex h-10 flex-auto grow-0 items-center rounded-sm border px-2.5 text-sm"
					>
						{i18n.categories[cause]}
					</Link>
				</WrappedLink>
			))}
		</ul>
	);
}
